import { useMemo, useState } from 'react';
import { Loader2, Star } from 'lucide-react';

import { useSaveSectionFavorite } from '@/hooks/useSectionFavorites';

import { scanSections } from './sections';

export default function SectionPicker({
  html,
  copyMeta,
  draftId,
  viewingSaved,
}: {
  html: string | undefined;
  copyMeta: unknown;
  draftId: string | undefined;
  viewingSaved: boolean;
}) {
  const scan = useMemo(
    () => scanSections(html ?? '', copyMeta),
    [html, copyMeta],
  );
  const [instanceId, setInstanceId] = useState('');
  const [name, setName] = useState('');
  const save = useSaveSectionFavorite();

  const chosen = scan.sections.find((s) => s.instanceId === instanceId) ?? null;
  const saved = save.data ?? null;

  if (!viewingSaved || !draftId) {
    return (
      <p className="text-xs text-muted-foreground">
        Open a saved page from History to favorite one of its sections. A run
        that has not been stored yet has no draft to take a section from.
      </p>
    );
  }

  if (scan.unavailable) {
    return (
      <p className="rounded border border-coral bg-coral-light px-3 py-2 text-xs text-coral-dark">
        {scan.unavailable}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs text-muted-foreground" htmlFor="fav-section">
        Section
      </label>
      <select
        id="fav-section"
        value={instanceId}
        onChange={(e) => {
          setInstanceId(e.target.value);
          save.reset();
        }}
        className="w-full rounded border border-border bg-card px-2 py-1.5 text-sm"
      >
        <option value="">Choose a band…</option>
        {scan.sections.map((s) => (
          <option key={s.instanceId} value={s.instanceId}>
            {s.label}
          </option>
        ))}
      </select>

      <label className="block text-xs text-muted-foreground" htmlFor="fav-name">
        Name
      </label>
      <input
        id="fav-name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="What makes this one worth keeping"
        className="w-full rounded border border-border bg-card px-2 py-1.5 text-sm"
      />

      <button
        type="button"
        disabled={!chosen || !name.trim() || save.isPending}
        onClick={() =>
          save.mutate({
            name: name.trim(),
            source_draft_id: draftId,
            section_instance_id: chosen!.instanceId,
            semantic_role: chosen!.semanticRole,
          })
        }
        className="inline-flex w-full items-center justify-center gap-2 rounded bg-coral
                   px-3 py-1.5 text-sm text-white hover:bg-coral-hover disabled:opacity-50"
      >
        {save.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Star className="h-4 w-4" />
        )}
        {save.isPending ? 'Saving…' : 'Save as favorite'}
      </button>

      {scan.unresolvable.length > 0 && (
        <div className="rounded border border-coral bg-coral-light px-2 py-1.5 text-xs text-coral-dark">
          <p className="font-medium">
            Not offered, because they could not be resolved:
          </p>
          <ul className="mt-1 space-y-0.5">
            {scan.unresolvable.map((u) => (
              <li key={u.instanceId}>
                {u.instanceId} — {u.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {scan.unclaimed > 0 && (
        <p className="text-xs text-muted-foreground">
          {scan.unclaimed} band{scan.unclaimed === 1 ? '' : 's'} in the stored
          HTML {scan.unclaimed === 1 ? 'is' : 'are'} not on this page&rsquo;s
          own section list and {scan.unclaimed === 1 ? 'is' : 'are'} not
          offered.
        </p>
      )}

      {save.error && (
        <p className="rounded border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
          Not saved: {(save.error as Error).message}
        </p>
      )}

      {saved && !save.isPending && (
        <p className="text-xs text-teal-dark">
          Saved as “{saved.name}”, pinned to this stored page.
        </p>
      )}
    </div>
  );
}
